import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Search } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { Table } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { apiError } from '@/lib/apiError';
import { listGroups, createGroup, alterGroup, deleteGroup, type AccGroup } from '@/api/accountsBooks';
import { groupTree } from './groupTree';

const NATURES = [
  { code: 'A', label: 'Assets' },
  { code: 'L', label: 'Liabilities' },
  { code: 'I', label: 'Income' },
  { code: 'E', label: 'Expenses' },
];

interface Draft { id: number | null; name: string; parentId: string; nature: string }

export function GroupsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);

  const query = useQuery({ queryKey: ['acc-books', 'groups'], queryFn: listGroups });
  const tree = useMemo(() => groupTree(query.data ?? []), [query.data]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? tree.filter((r) => r.g.name.toLowerCase().includes(q)) : tree;
  }, [tree, search]);

  const saveMut = useMutation({
    mutationFn: (d: Draft) => {
      const parentId = d.parentId ? Number(d.parentId) : null;
      const body = { name: d.name.trim(), parentId, nature: parentId ? null : d.nature };
      return d.id ? alterGroup(d.id, body) : createGroup(body);
    },
    onSuccess: () => {
      toast.push('Group saved', 'success');
      setDraft(null);
      qc.invalidateQueries({ queryKey: ['acc-books', 'groups'] });
    },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });

  const delMut = useMutation({
    mutationFn: (id: number) => deleteGroup(id),
    onSuccess: () => {
      toast.push('Group deleted', 'success');
      qc.invalidateQueries({ queryKey: ['acc-books', 'groups'] });
    },
    onError: (e) => toast.push(apiError(e, 'Delete failed'), 'error'),
  });

  const openAlter = (g: AccGroup) =>
    setDraft({ id: g.id, name: g.name, parentId: g.parentId ? String(g.parentId) : '', nature: NATURES.find((n) => n.label === g.nature)?.code ?? '' });

  return (
    <div>
      <SectionHeader title="Groups" />

      <FilterBar>
        <FilterField label="Search" className="sm:w-[300px]">
          <div className="relative">
            <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search group" className="!pl-8" />
          </div>
        </FilterField>
        <Button variant="primary" size="sm" className="sm:ml-auto" icon={<Plus size={12} />} onClick={() => setDraft({ id: null, name: '', parentId: '', nature: '' })}>
          Create Group
        </Button>
      </FilterBar>

      <Table<{ g: AccGroup; depth: number }>
        storageKey="acc-books-groups"
        title="Groups"
        count={`${rows.length} group${rows.length === 1 ? '' : 's'}`}
        rows={rows}
        rowKey={(r) => r.g.id}
        loading={query.isFetching && tree.length === 0}
        empty="No groups found."
        columns={[
          {
            key: 'name', header: 'Name', minWidth: '240px',
            cell: ({ g, depth }) => (
              <span style={{ paddingLeft: depth * 16 }} className="inline-flex items-center gap-1.5">
                <strong className={depth === 0 ? '' : 'font-semibold'}>{g.name}</strong>
                {g.isSystem && <span className="rounded border border-accent/20 bg-accent/10 px-1.5 py-px text-[10px] font-semibold text-accent">Predefined</span>}
              </span>
            ),
          },
          { key: 'under', header: 'Under', width: '200px', cell: ({ g }) => <span className={g.parentName ? '' : 'text-[var(--text-muted)]'}>{g.parentName ?? 'Primary'}</span> },
          { key: 'nature', header: 'Nature', width: '120px', cell: ({ g }) => g.nature },
          { key: 'ledgers', header: 'Ledgers', width: '90px', align: 'right', cell: ({ g }) => g.ledgers },
          {
            key: 'actions', header: '', width: '130px', align: 'right', alwaysVisible: true,
            cell: ({ g }) => g.isSystem ? null : (
              <div className="flex items-center justify-end gap-1">
                <Button variant="glass" size="sm" onClick={() => openAlter(g)}>Alter</Button>
                <button type="button" onClick={async () => { if (await confirmDlg(`Delete group ${g.name}?`)) delMut.mutate(g.id); }} className="rounded p-1.5 text-danger hover:bg-danger/10" title="Delete group"><Trash2 size={14} /></button>
              </div>
            ),
          },
        ]}
      />

      <Modal
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?.id ? `Alter Group ${draft.name}` : 'Create Group'}
        size="sm"
        footer={
          <>
            <Button variant="glass" onClick={() => setDraft(null)}>Cancel</Button>
            <Button variant="primary" disabled={!draft?.name.trim() || saveMut.isPending} onClick={() => draft && saveMut.mutate(draft)}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">Name</label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} maxLength={100} autoFocus />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">Under</label>
              <Select value={draft.parentId} onChange={(e) => setDraft({ ...draft, parentId: e.target.value })}>
                <option value="">Primary</option>
                {tree.filter(({ g }) => g.id !== draft.id).map(({ g, depth }) => (
                  <option key={g.id} value={g.id}>{'  '.repeat(depth)}{g.name}</option>
                ))}
              </Select>
            </div>
            {!draft.parentId && (
              <div>
                <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">Nature</label>
                <Select value={draft.nature} onChange={(e) => setDraft({ ...draft, nature: e.target.value })}>
                  <option value="">Choose…</option>
                  {NATURES.map((n) => <option key={n.code} value={n.code}>{n.label}</option>)}
                </Select>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
